import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { RouteKey } from '@/i18n/routes'

interface Sector {
  key: string
  title: string
  description: string
  to: RouteKey
  image: string
  features: string[]
}

export function SectorsGridSection() {
  const { t } = useTranslation()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  })

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '20%'])

  const sectors: Sector[] = [
    {
      key: 'industrialAutomation',
      title: t('nav.industrialAutomation'),
      description: t('sectors.industrialAutomationDesc'),
      to: 'industrialAutomation',
      image: '/media/images/vetro-cavo-impianto-fg-automazione.webp',
      features: [
        t('homepage.sectors.features.hollowGlass'),
        t('homepage.sectors.features.wasteTreatment'),
        t('homepage.sectors.features.extraction'),
        t('homepage.sectors.features.wood'),
      ],
    },
    {
      key: 'aiVision',
      title: t('nav.aiVision'),
      description: t('sectors.aiVisionDesc'),
      to: 'aiVision',
      image: '/media/images/AI.jpg',
      features: [
        t('homepage.sectors.features.brainyware'),
        t('homepage.sectors.features.visiofy'),
        t('homepage.sectors.features.qualityControl'),
        t('homepage.sectors.features.dataExtraction'),
      ],
    },
    {
      key: 'industrialDistribution',
      title: t('nav.industrialDistribution'),
      description: t('sectors.industrialDistributionDesc'),
      to: 'industrialDistribution',
      image: '/media/images/DISTRIBUZ.-INDUSTRIALE-CABINE-MEDIA-TENSIONE.jpg',
      features: [
        t('homepage.sectors.features.mvSubstations'),
        t('homepage.sectors.features.powerCenter'),
        t('homepage.sectors.features.lighting'),
      ],
    },
    {
      key: 'tertiarySector',
      title: t('nav.tertiarySector'),
      description: t('sectors.tertiarySectorDesc'),
      to: 'tertiarySector',
      image: '/media/images/SETTORE-TERZIARIO-RETI-CABLATE.jpg',
      features: [
        t('homepage.sectors.features.networks'),
        t('homepage.sectors.features.fireSafety'),
        t('homepage.sectors.features.intrusion'),
        t('homepage.sectors.features.homeAutomation'),
      ],
    },
  ]

  return (
    <section ref={ref} id="sectors" className="section relative overflow-hidden bg-[var(--color-ink)]">
      {/* Background Elements */}
      <motion.div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, var(--color-graphite) 1px, transparent 0)`,
          backgroundSize: '48px 48px',
          y: backgroundY,
        }}
      />

      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-void)] via-transparent to-[var(--color-void)]" />

      {/* Accent Glow */}
      <div className="absolute -left-40 top-1/2 h-[600px] w-[600px] -translate-y-1/2 rounded-full bg-[var(--accent-primary)]/5 blur-[150px]" />

      <div className="container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 text-center"
        >
          <div className="section-label justify-center">
            <span className="label">Settori</span>
          </div>
          <h2 className="headline-xl mt-6">
            {t('homepage.sectors.title')}
          </h2>
          <p className="body-lg mx-auto mt-6 max-w-2xl">
            {t('homepage.sectors.subtitle')}
          </p>
        </motion.div>

        {/* Sectors Grid - Asymmetric Layout */}
        <div className="grid gap-6 lg:grid-cols-2">
          {sectors.map((sector, index) => (
            <motion.div
              key={sector.key}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.7,
                delay: 0.2 + index * 0.1,
                ease: [0.16, 1, 0.3, 1]
              }}
            >
              <LocalizedLink to={sector.to}>
                <article className="group relative overflow-hidden rounded-xl bg-[var(--color-charcoal)] transition-all duration-500 hover:shadow-2xl hover:shadow-black/40">
                  {/* Image Container */}
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={sector.image}
                      alt={sector.title}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[var(--color-charcoal)] via-[var(--color-charcoal)]/60 to-transparent" />

                    {/* Index Number */}
                    <div className="absolute right-4 top-4 font-mono text-6xl font-bold text-white/5">
                      0{index + 1}
                    </div>

                    {/* Content Overlay */}
                    <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-8">
                      {/* Features Tags */}
                      <div className="mb-4 flex flex-wrap gap-2">
                        {sector.features.slice(0, 3).map((feature) => (
                          <span
                            key={feature}
                            className="tag"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      {/* Title */}
                      <h3 className="headline-md text-white">
                        {sector.title}
                      </h3>

                      {/* Description */}
                      <p className="body-sm mt-2 line-clamp-2 text-[var(--color-ash)]">
                        {sector.description}
                      </p>

                      {/* CTA - Appears on Hover */}
                      <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-[var(--accent-primary)] opacity-0 transition-all duration-500 group-hover:opacity-100">
                        {t('homepage.sectors.cta')}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>

                  {/* Bottom Border Animation */}
                  <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-[var(--accent-primary)] transition-all duration-700 group-hover:w-full" />
                </article>
              </LocalizedLink>
            </motion.div>
          ))}
        </div>

        {/* Renewable Energy Card - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6"
        >
          <LocalizedLink to="renewableEnergy">
            <article className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-emerald-900/20 to-[var(--color-charcoal)] p-8 transition-all duration-500 hover:shadow-2xl hover:shadow-black/40 md:p-10">
              <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
                <div className="flex-1">
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-emerald-400">
                    {t('homepage.sectors.renewable.badge')}
                  </span>
                  <h3 className="headline-lg mt-4">
                    {t('nav.renewableEnergy')}
                  </h3>
                  <p className="body-md mt-2 max-w-xl">
                    {t('sectors.renewableEnergyDesc')}
                  </p>
                </div>

                {/* CTA Button */}
                <div className="flex items-center gap-3 text-sm font-semibold text-emerald-400 transition-all duration-300 group-hover:gap-4">
                  {t('homepage.sectors.renewable.cta')}
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 transition-all group-hover:bg-emerald-500/20">
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -right-20 -top-20 h-40 w-40 rounded-full bg-emerald-500/5 blur-3xl" />
            </article>
          </LocalizedLink>
        </motion.div>
      </div>
    </section>
  )
}
